import logger from '@leaflink/snitch';

import { hashCode } from './helpers';

const PREFIX = 'll';

/**
 * Persist an item to localStorage, unique to each url pathname unless [options.global] is set to true
 *
 * If global is set to false (the default):
 *   name: 'foo'
 *   data: { a: 'a' }
 *
 *   localStorage key: 'll-foo-/c/the-leaf-shop/customers/'
 *   localStorage value: { a: 'a' }
 *
 * If global is set to true:
 *   name: 'bar'
 *   data: { b: 'b' }
 *
 *   localStorage key: 'll-bar'
 *   localStorage value: { b: 'b' }
 *
 * @param {string} name - the storage key name, often a component name
 * @param {object} data - the data to persist
 * @param {object} [options] - an optional options object
 * @param {boolean} [options.global] - does not add url to the key when true
 */
export function setPersistentItem(name, data, options: { global?: boolean } = {}) {
  const scope = options.global ? '' : `-${location.pathname}`;
  const storageKey = `${PREFIX}-${name}${scope}`;
  const storageValue = JSON.stringify(data);

  try {
    localStorage.setItem(storageKey, storageValue);
  } catch (error) {
    if ((error as Error)?.name === 'QuotaExceededError') {
      logger.log(`Your localStorage is full. Failed to save item for key: ${storageKey}`);
    } else {
      logger.log(`Failed to save value to localStorage for key: ${storageKey}`);
    }
  }
}

/**
 * Retrieve an item from localStorage, previously saved by `setPersistentItem`.
 * @param {string} name The storage key name, often a component name
 * @param {object} [options] - an optional options object
 * @param {boolean} [options.global] - does not add url to the key when true
 * @returns {any | undefined}
 */
export function getPersistentItem(name, options: { global?: boolean } = {}) {
  const scope = options.global ? '' : `-${location.pathname}`;
  const item = localStorage.getItem(`${PREFIX}-${name}${scope}`);

  // localStorage.getItem always returns a string or null. When it's a string,
  // it usually contains an object or a boolean. If the value is 'false', we
  // need to ensure we return false instead of null.
  if (item === null || item === 'undefined') {
    return null;
  }

  try {
    return JSON.parse(item);
  } catch (err) {
    logger.error((err as Error)?.message, { error: err });

    return null;
  }
}

/**
 * Remove an item from localStorage, previously saved by `setPersistentItem`.
 * @param {string} name The storage key name, often a component name
 * @param {object} [options] - an optional options object
 * @param {boolean} [options.global] - does not add url to the key when true
 */
export function removePersistentItem(name, options: { global?: boolean } = {}) {
  const scope = options.global ? '' : `-${location.pathname}`;
  const storageKey = `${PREFIX}-${name}${scope}`;
  const item = localStorage.getItem(storageKey);

  if (item === null) {
    return;
  }

  try {
    localStorage.removeItem(storageKey);
  } catch (err) {
    logger.error((err as Error).message, { error: err });
  }
}

/**
 ***************** DEPRECATED *****************
 */
export function getPersistentItem__deprecated(itemKey: string, name) {
  const item = JSON.parse(localStorage.getItem(itemKey) || '{}');

  try {
    return (item[name] || {})[hashCode(location.pathname)];
  } catch (err) {
    logger.error((err as Error)?.message, { error: err });

    return undefined;
  }
}

export function removePersistentItem__deprecated(itemKey, name) {
  const item = JSON.parse(localStorage.getItem(itemKey) || '{}');
  const itemValue = { ...item };

  if (itemValue[name]) {
    delete itemValue[name][hashCode(location.pathname)];
  }

  try {
    localStorage.setItem(itemKey, JSON.stringify(itemValue));
  } catch (error) {
    if ((error as Error)?.name === 'QuotaExceededError') {
      logger.log(`Your localStorage is full. Failed to set item.`);
    } else {
      logger.log(`Failed to set item to localStorage.`);
    }
  }
}
// END DEPRECATED

export const persistentStorage = {
  setItem: setPersistentItem,
  getItem: getPersistentItem,
  removeItem: removePersistentItem,
};
